/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { setClassNames } from 'ddc-classnames-js';
import { usePrefs, useRequestData, useSitemap } from 'wsm-common-data';
import { FetchReviewsContext } from '../providers/FetchReviewsProvider';
import {
	checkIfLastPage,
	generatePageList,
	stateToSearchParams
} from '../helpers';
import ArrowNavButton from './ArrowNavButton';
import PageCountButton from './PageCountButton';

const PageNavigation = () => {
	// hooks
	/**
	 * @type {PageNavigationReduxState}
	 */
	const { page, pageReviewsCount, currentTotalRatings, reviewsFilters } =
		useSelector((state) => ({
			reviewsFilters: state.reviewsFilters,
			page: state.page,
			pageReviewsCount: state.reviews.length,
			currentTotalRatings: state.reviewsInfo.currentTotalRatings
		}));
	const { reviewsPerPage } = usePrefs();
	const { isLoading, fetchReviews } = useContext(FetchReviewsContext);
	const { deviceType } = useRequestData();
	const sitemap = useSitemap();
	// variables
	const pagePath = sitemap.get('VW_CUSTOMER_REVIEWS');
	const isDesktop = deviceType === 'DESKTOP';
	// +page casts page to a number from a string
	const currPage = +page;
	// Important!: currentTotalRatings is not always accurate, that is why we need to use
	// pageReviewsCount to check if the current page is the last page.
	const isLastPage = checkIfLastPage(
		currPage,
		reviewsPerPage,
		pageReviewsCount,
		currentTotalRatings
	);
	const enablePreviousButton = currPage > 1 && !isLoading;
	const enableNextButton = !isLastPage && !isLoading;
	const pages = generatePageList(
		currPage,
		Math.ceil(currentTotalRatings / reviewsPerPage)
	);
	// Shows intermediate pages if the device is desktop
	const showIntermediatePages = isDesktop;
	const previousPageNumber = currPage > 1 ? currPage - 1 : 1;
	const previousButtonSearchParam = stateToSearchParams(reviewsFilters, {
		page: previousPageNumber
	});
	const nextButtonSearchParam = stateToSearchParams(reviewsFilters, {
		page: currPage + 1
	});
	const previousPageLink = `${pagePath}?${previousButtonSearchParam.toString()}`;
	const nextPageLink = `${pagePath}?${nextButtonSearchParam.toString()}`;
	// handlers
	const handleNextPage = async (e) => {
		// do default behavior if metaKey or ctrlKey is pressed
		if (e.metaKey || e.ctrlKey) {
			return;
		}
		e.preventDefault();
		if (!isLastPage) {
			const newPage = currPage + 1;
			await fetchReviews(newPage, {});
		}
	};
	const handlePreviousPage = async (e) => {
		// do default behavior if metaKey or ctrlKey is pressed
		if (e.metaKey || e.ctrlKey) {
			return;
		}
		e.preventDefault();
		if (page > 1) {
			const newPage = currPage - 1;
			await fetchReviews(newPage, {});
		}
	};
	const handlePageClick = async (e) => {
		if (e.metaKey || e.ctrlKey) {
			return;
		}
		e.preventDefault();

		const selectedPage = Number(e.target.text);

		if (Number.isNaN(selectedPage)) return;
		if (selectedPage === currPage) return;
		if (
			selectedPage < 1 ||
			selectedPage > Math.ceil(currentTotalRatings / reviewsPerPage)
		)
			return;
		await fetchReviews(selectedPage, {});
	};

	// classes
	// ul classes
	const ulClasses = [
		'pagination',
		'page-navigation__button-list',
		'list-unstyled',
		'd-flex',
		'mt-5',
		'w-100',
		'justify-content-center row-gap'
	];

	// button classes
	const commonDirectionButtonClasses = [
		isDesktop ? 'ddc-font-size-medium' : 'ddc-font-size-xlarge'
	];
	const previousButtonClasses = [
		...commonDirectionButtonClasses,
		'pagination-previous',
		enablePreviousButton ? '' : 'disabled'
	];
	const nextButtonClasses = [
		...commonDirectionButtonClasses,
		'pagination-next',
		enableNextButton ? '' : 'disabled'
	];

	return (
		<nav className="page-navigation" aria-label="Reviews Navigation">
			<ul className={setClassNames(ulClasses)}>
				<li className={setClassNames(previousButtonClasses)}>
					<ArrowNavButton
						direction="left"
						name="previous"
						isButtonEnabled={enablePreviousButton}
						pageLink={previousPageLink}
						onClick={handlePreviousPage}
					/>
				</li>
				{showIntermediatePages &&
					pages.map((pageNumber, index) => {
						const pageButtonLink = stateToSearchParams(
							reviewsFilters,
							{
								page: pageNumber
							}
						);
						const linkClasses = [
							pageNumber === currPage ? 'active' : '',
							isLoading ? 'disabled opacity-50' : '',
							pageNumber === '...' ? 'disabled' : ''
						];
						const key =
							pageNumber === '...'
								? `${index}-${pageNumber}`
								: pageNumber;

						return (
							<li
								className={setClassNames(linkClasses)}
								key={key}
							>
								<PageCountButton
									pageNumber={pageNumber}
									pagePath={pagePath}
									pageButtonLink={pageButtonLink.toString()}
									handlePageClick={handlePageClick}
								/>
							</li>
						);
					})}
				<li className={setClassNames(nextButtonClasses)}>
					<ArrowNavButton
						direction="right"
						name="next"
						isButtonEnabled={enableNextButton}
						pageLink={nextPageLink}
						onClick={handleNextPage}
					/>
				</li>
			</ul>
		</nav>
	);
};

export default PageNavigation;
